/**
 * The floating panel a map shows instead of itself — board 6, tiles 1 and 2.
 *
 * Two states need it and they are the same card: *No map location* and
 * *Aerial imagery isn't loading*. 380px, 18px of padding, a 38px tinted glyph
 * tile, a 15px title, a sentence, then a row of equal buttons with the
 * recovering one green and last.
 *
 * It sits over a grid, never over an empty white box: a map that cannot draw
 * itself should still look like the place a map goes, so the surface behind
 * carries the 36px rule the mockup draws.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { useCoarse } from '../../lib/use-narrow.js'

/**
 * The 36px grid the mockup draws where a map would be.
 *
 * In tokens rather than the mockup's hexes: `surface-group` for the plate and
 * `line` for the rules, so the placeholder follows dark mode like everything
 * else. `rgb(var(…))` because the tokens are bare `R G B` triples and a
 * triple is not a colour — the same trap `markTint` fell into.
 */
export function MapGrid() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 bg-surface-group"
      style={{
        backgroundImage:
          'linear-gradient(rgb(var(--color-line)) 1px, transparent 1px),'
          + ' linear-gradient(90deg, rgb(var(--color-line)) 1px, transparent 1px)',
        backgroundSize: '36px 36px',
      }}
    />
  )
}

/** A bordered card the size of a map, for a tab that has no editor around it. */
export function MapCanvasFrame({ children }: { children: ReactNode }) {
  return (
    <div className={cn(
      'relative min-h-[330px] flex-1 overflow-hidden rounded-record border border-line',
      'bg-surface',
    )}
    >
      <MapGrid />
      <div className="absolute inset-0 flex items-center justify-center p-5">{children}</div>
    </div>
  )
}

export function MapPanel({
  tone = 'quiet', icon, title, body, actions,
}: {
  tone?: 'quiet' | 'warn'
  icon: ReactNode
  title: string
  body: ReactNode
  actions: ReactNode
}) {
  return (
    <div className={cn(
      /* `relative`: it sits over `MapGrid`, which is absolutely positioned
         and would otherwise paint on top of a static sibling. */
      'relative flex w-[380px] max-w-full flex-col gap-2.5 rounded-touch border border-line',
      'bg-surface p-[18px] shadow-popover',
    )}
    >
      <span className={cn(
        'inline-flex h-[38px] w-[38px] items-center justify-center rounded-tile-large',
        tone === 'warn' ? 'bg-warn-bg text-warn' : 'bg-surface-group text-text-body',
      )}
      >
        {icon}
      </span>
      <b className="text-[15px] font-bold">{title}</b>
      <div className="text-cell leading-[1.5] text-text-muted">{body}</div>
      {actions}
    </div>
  )
}

/** The button row: equal widths, 36px, the recovering action green and last. */
/**
 * The panel's buttons, 36px on a pointer and 44 under a thumb (§7).
 *
 * The height is set here rather than on each caller because every one of
 * these panels is *the* thing on the screen when it is up — the imagery has
 * failed, or there is no location yet — so a button that is hard to hit is a
 * screen with no way out of it.
 */
export function PanelActions({ children }: { children: ReactNode }) {
  const coarse = useCoarse()
  return (
    <div className={cn(
      'mt-1 flex gap-2 [&>button]:flex-1',
      coarse ? '[&>button]:h-11' : '[&>button]:h-9',
    )}
    >
      {children}
    </div>
  )
}
